import BuildIcon from "@mui/icons-material/Build"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded"
import HelpCenterIcon from "@mui/icons-material/HelpCenter"
import HourglassBottomRoundedIcon from "@mui/icons-material/HourglassBottomRounded"
import LinearScaleIcon from "@mui/icons-material/LinearScale"
import ModeEditIcon from "@mui/icons-material/ModeEdit"
import NextPlanRoundedIcon from "@mui/icons-material/NextPlanRounded"
import PsychologyIcon from "@mui/icons-material/Psychology"
import RuleIcon from "@mui/icons-material/Rule"
import SendIcon from "@mui/icons-material/Send"
import ThumbUpIcon from "@mui/icons-material/ThumbUp"
import UpdateIcon from "@mui/icons-material/Update"
import WarningRoundedIcon from "@mui/icons-material/WarningRounded"
import { AlertColor, SvgIcon } from "@mui/material"
import { ReactElement } from "react"

import {
    ApplicationStatuses,
    InstallerApplicationStatuses,
} from "../../../constants/enums"
import {
    APPROVED,
    COMPLIANT,
    NON_COMPLIANT,
    AWAITING_ASSESSMENT,
    NON_COMPLIANT_NEW,
    NON_COMPLIANT_MINOR,
    COMPLAINT_CONFORMATION,
    UNKNOWN_STATUS,
    DRAFT_DEVICE,
    DRAFT_AMENDMENT,
} from "../../../constants/generalConstants"
import {
    TT_APPROVED,
    TT_COMPLAINT,
    TT_NON_COMPLIANT,
    TT_NON_COMPLIANT_NEW,
    TT_NON_COMPLIANT_MINOR,
    TT_COMPLAINT_CONFORMATION,
    TT_AWAITING_ASSESSMENT,
    TT_DNO_APPROVED,
    TT_DNO_APPROVED_WITH_LIMIT,
    TT_DNO_FURTHER_INFO_REQUIRED,
    TT_DNO_WORKS_REQUIRED,
    TT_ESCALATED_TO_DNO,
    TT_FAILED_TOO_LARGE,
    TT_FAILED,
    TT_IMAGE_SCANNING,
    TT_IMAGE_SCAN_FAILED,
    TT_INVALID_CUT_OUT,
    TT_CX_SUBMITTED,
    TT_CX_UPDATE_REQUIRED,
    TT_RE_AUTO_APPROVED,
    TT_RE_ESCALATED_TO_DNO,
    TT_CX_ACCEPTED,
} from "../../../constants/tooltips"

export type TStatusMetadataConfig = {
    color: "success" | "secondary" | "primary" | "error" | "warning"
    icon?: typeof SvgIcon
    iconAriaLabel?: string
    label: string
    tooltip: string
    alertSeverity: AlertColor
    alertMessage: ReactElement
}

type TStatusMetadataDispatcher = {
    [key: string]: TStatusMetadataConfig
}

export const STATUS_METADATA_DISPATCHER: TStatusMetadataDispatcher = {
    [COMPLIANT]: {
        color: "success",
        icon: ThumbUpIcon,
        iconAriaLabel: "compliant device",
        label: COMPLIANT,
        tooltip: TT_COMPLAINT,
        alertSeverity: "success",
        alertMessage: (
            <>
                This device has been assessed by ENA&apos;s engineers and has
                been deemed <strong>acceptable</strong> for use by Distribution
                network operators
            </>
        ),
    },
    [APPROVED]: {
        color: "success",
        icon: ThumbUpIcon,
        iconAriaLabel: "approved",
        label: APPROVED,
        tooltip: TT_APPROVED,
        alertSeverity: "success",
        alertMessage: <>Approved</>,
    },
    [NON_COMPLIANT]: {
        color: "error",
        icon: ErrorOutlineIcon,
        iconAriaLabel: "non-complaint device",
        label: NON_COMPLIANT,
        tooltip: TT_NON_COMPLIANT,
        alertSeverity: "error",
        alertMessage: (
            <>
                This device has been assessed by ENA&apos;s engineers as&nbsp;
                <strong>non-compliant</strong>. Distribution network operators
                are unlikely to accept this equipment on their networks
            </>
        ),
    },
    [AWAITING_ASSESSMENT]: {
        color: "primary",
        icon: HourglassBottomRoundedIcon,
        iconAriaLabel: "awaiting assessment",
        label: AWAITING_ASSESSMENT,
        tooltip: TT_AWAITING_ASSESSMENT,
        alertSeverity: "info",
        alertMessage: (
            <>
                This device is currently undergoing assessment by ENA&apos;s
                engineers. This can take 1-6 weeks from the date of submission.
                The status will be updated once the assessment is completed
            </>
        ),
    },
    [NON_COMPLIANT_NEW]: {
        color: "error",
        icon: ErrorRoundedIcon,
        iconAriaLabel: "Non-compliant for new installations",
        label: NON_COMPLIANT_NEW,
        tooltip: TT_NON_COMPLIANT_NEW,
        alertSeverity: "error",
        alertMessage: (
            <>
                This device has been assessed by ENA&apos;s engineers as&nbsp;
                <strong>non-compliant for new installations</strong> as it does
                not meet the latest standards. Distribution network operators
                are unlikely to accept this equipment on their networks.
            </>
        ),
    },
    [NON_COMPLIANT_MINOR]: {
        color: "warning",
        icon: WarningRoundedIcon,
        iconAriaLabel: "Minor non-compliance",
        label: NON_COMPLIANT_MINOR,
        tooltip: TT_NON_COMPLIANT_MINOR,
        alertSeverity: "warning",
        alertMessage: (
            <>
                This device has been assessed by ENA&apos;s engineers and
                a&nbsp;
                <strong>minor non-compliance</strong> issue was identified
            </>
        ),
    },
    [COMPLAINT_CONFORMATION]: {
        color: "success",
        icon: NextPlanRoundedIcon,
        iconAriaLabel: "Compliant - DNO confirmation required",
        label: COMPLAINT_CONFORMATION,
        tooltip: TT_COMPLAINT_CONFORMATION,
        alertSeverity: "success",
        alertMessage: (
            <>
                This device has been assessed by ENA&apos;s engineers and deemed{" "}
                <strong> partially compliant</strong>. Distribution network
                operators will require further testing on-site before connection
            </>
        ),
    },
    [ApplicationStatuses.autoApproved]: {
        color: "success",
        icon: ThumbUpIcon,
        iconAriaLabel: "approved",
        label: ApplicationStatuses.autoApproved,
        tooltip: TT_APPROVED,
        alertSeverity: "success",
        alertMessage: <>Approved</>,
    },
    Current: {
        color: "success",
        label: "Current",
        tooltip: "This plug-in solar device is active at this address",
        alertSeverity: "success",
        alertMessage: <>Current</>,
    },
    Historic: {
        color: "secondary",
        label: "Historic",
        tooltip:
            "This plug-in solar device is no longer active at this address",
        alertSeverity: "info",
        alertMessage: <>Historic</>,
    },
    [ApplicationStatuses.dnoApproved]: {
        color: "success",
        icon: ThumbUpIcon,
        iconAriaLabel: "Dno Approved",
        label: ApplicationStatuses.dnoApproved,
        tooltip: TT_DNO_APPROVED,
        alertSeverity: "success",
        alertMessage: <>Dno Approved</>,
    },
    [ApplicationStatuses.dnoApprovedWithLimit]: {
        color: "success",
        icon: RuleIcon,
        iconAriaLabel: "Dno Approved With Limit",
        label: "Approved With Limit",
        tooltip: TT_DNO_APPROVED_WITH_LIMIT,
        alertSeverity: "success",
        alertMessage: <>Dno Approved With Limit</>,
    },
    [ApplicationStatuses.dnoFurtherInfoRequired]: {
        color: "warning",
        icon: HelpCenterIcon,
        iconAriaLabel: "Dno Installer Info Required",
        label: "Installer Info Required",
        tooltip: TT_DNO_FURTHER_INFO_REQUIRED,
        alertSeverity: "warning",
        alertMessage: <>Installer Information Required</>,
    },
    [ApplicationStatuses.dnoWorksRequired]: {
        color: "warning",
        icon: BuildIcon,
        iconAriaLabel: "DNO Works Required",
        label: "DNO Works Required",
        tooltip: TT_DNO_WORKS_REQUIRED,
        alertSeverity: "warning",
        alertMessage: <>DNO Works Required</>,
    },
    [ApplicationStatuses.escalatedToDno]: {
        color: "primary",
        icon: SendIcon,
        iconAriaLabel: "Escalated to DNO",
        label: "Escalated to DNO",
        tooltip: TT_ESCALATED_TO_DNO,
        alertSeverity: "info",
        alertMessage: <>Escalated to DNO</>,
    },
    [InstallerApplicationStatuses.reEscalatedToDno]: {
        color: "primary",
        icon: SendIcon,
        iconAriaLabel: InstallerApplicationStatuses.reEscalatedToDno,
        label: InstallerApplicationStatuses.reEscalatedToDno,
        tooltip: TT_RE_ESCALATED_TO_DNO,
        alertSeverity: "info",
        alertMessage: <>Escalated to DNO after resubmission</>,
    },
    [InstallerApplicationStatuses.underAssessment]: {
        color: "primary",
        icon: LinearScaleIcon,
        iconAriaLabel: "Under Assessment",
        label: "Under Assessment",
        tooltip: TT_AWAITING_ASSESSMENT,
        alertSeverity: "info",
        alertMessage: <>Under Assessment</>,
    },
    [InstallerApplicationStatuses.failed]: {
        color: "error",
        icon: SendIcon,
        iconAriaLabel: "Failed",
        label: "Application Failed",
        tooltip: TT_FAILED,
        alertSeverity: "error",
        alertMessage: <>Failed</>,
    },
    [InstallerApplicationStatuses.failedToLarge]: {
        color: "error",
        icon: ErrorOutlineIcon,
        iconAriaLabel: "Failed",
        label: "Failed Too Large",
        tooltip: TT_FAILED_TOO_LARGE,
        alertSeverity: "error",
        alertMessage: <>Failed - Too Large</>,
    },
    [InstallerApplicationStatuses.imageScanning]: {
        color: "primary",
        icon: PsychologyIcon,
        iconAriaLabel: InstallerApplicationStatuses.imageScanning,
        label: InstallerApplicationStatuses.imageScanning,
        tooltip: TT_IMAGE_SCANNING,
        alertSeverity: "info",
        alertMessage: <>Scanning Cut-Out Image</>,
    },
    [InstallerApplicationStatuses.imageScanFailed]: {
        color: "error",
        icon: ErrorOutlineIcon,
        iconAriaLabel: InstallerApplicationStatuses.imageScanFailed,
        label: InstallerApplicationStatuses.imageScanFailed,
        tooltip: TT_IMAGE_SCAN_FAILED,
        alertSeverity: "error",
        alertMessage: <>Scanning Cut-Out Image Failed</>,
    },
    [InstallerApplicationStatuses.invalidCutOut]: {
        color: "error",
        icon: ErrorOutlineIcon,
        iconAriaLabel: InstallerApplicationStatuses.invalidCutOut,
        label: InstallerApplicationStatuses.invalidCutOut,
        tooltip: TT_INVALID_CUT_OUT,
        alertSeverity: "error",
        alertMessage: <>You have provided an invalid cut-out image. O</>,
    },
    [InstallerApplicationStatuses.cxSubmitted]: {
        color: "primary",
        icon: HourglassBottomRoundedIcon,
        iconAriaLabel: InstallerApplicationStatuses.cxSubmitted,
        label: InstallerApplicationStatuses.cxSubmitted,
        tooltip: TT_CX_SUBMITTED,
        alertSeverity: "info",
        alertMessage: <>Commissioning information has been submitted</>,
    },
    [InstallerApplicationStatuses.cxAccepted]: {
        color: "success",
        icon: ThumbUpIcon,
        iconAriaLabel: InstallerApplicationStatuses.cxAccepted,
        label: InstallerApplicationStatuses.cxAccepted,
        tooltip: TT_CX_ACCEPTED,
        alertSeverity: "success",
        alertMessage: <>Commissioning information accepted by DNO</>,
    },
    [InstallerApplicationStatuses.cxUpdateRequired]: {
        color: "warning",
        icon: UpdateIcon,
        iconAriaLabel: InstallerApplicationStatuses.cxUpdateRequired,
        label: InstallerApplicationStatuses.cxUpdateRequired,
        tooltip: TT_CX_UPDATE_REQUIRED,
        alertSeverity: "warning",
        alertMessage: (
            <>
                DNO has reviewed commissioning information and has requested
                some changes
            </>
        ),
    },
    [InstallerApplicationStatuses.reAutoApproved]: {
        color: "success",
        icon: ThumbUpIcon,
        iconAriaLabel: InstallerApplicationStatuses.reAutoApproved,
        label: InstallerApplicationStatuses.reAutoApproved,
        tooltip: TT_RE_AUTO_APPROVED,
        alertSeverity: "success",
        alertMessage: (
            <>
                Application has been re-submitted and auto-approved by Connect
                Direct
            </>
        ),
    },
    [UNKNOWN_STATUS]: {
        color: "secondary",
        icon: LinearScaleIcon,
        iconAriaLabel: "unknown status",
        tooltip: "",
        label: "",
        alertSeverity: "info",
        alertMessage: <>Status is unrecognised</>,
    },
    [DRAFT_DEVICE]: {
        color: "warning",
        icon: ModeEditIcon,
        iconAriaLabel: "Draft Device",
        label: "Draft Device",
        tooltip: "",
        alertSeverity: "warning",
        alertMessage: (
            <>
                This device is currently in draft mode so some details may be
                missing. You can finish the draft to submit the device for
                assessment
            </>
        ),
    },
    [DRAFT_AMENDMENT]: {
        color: "warning",
        icon: ModeEditIcon,
        iconAriaLabel: "Draft Amendment",
        label: "Draft Amendment",
        tooltip: "",
        alertSeverity: "warning",
        alertMessage: (
            <>
                This device is currently in draft mode so some details may be
                missing. You can finish the draft to submit the device for
                assessment
            </>
        ),
    },
}
